/// <amd-module name="Repos/CommitActions/Components/SearchForCommitPanel" />
import * as React from "react";
import * as Resources from "Repos/CommitActions/Resources";
import { GitBranchStats, GitQueryBranchStatsCriteria, GitRepository, GitVersionDescriptor, GitVersionType } from "Repos/Common/Contracts/Git";
import { isBranch, refToBranchName, refToTagName } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor, commitToVersionDescriptor, tagToVersionDescriptor } from "Repos/Common/Util/Version";
import { VersionDropdown } from "Repos/VersionDropdown/Components/VersionDropdown";
import { IVersionDropdownItem } from "Repos/VersionDropdown/Components/VersionDropdown.Props";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { FormItem } from "VSSUI/FormItem";
import { Icon } from "VSSUI/Icon";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { ITableColumn, ITableRow, SimpleTableCell, Table } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ISearchStatusItem } from "../Contracts";
import { getGitClient, IGitRestClient } from "../RestClient/Git";

export interface ISearchForCommitPanelProps {
    commitId: string;
    initialTargetBranch: string;
    onActivate: (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<ISearchStatusItem>) => void;
    onDismiss: () => void;
    repository: GitRepository;
}

export class SearchForCommitPanel extends VssComponent<ISearchForCommitPanelProps> {
    private readonly errorMessage = new ObservableValue("");
    private filterText: string;
    private statusItems: ObservableArray<ISearchStatusItem>;
    private searchedItems: string[];

    constructor(props: ISearchForCommitPanelProps, context: IVssComponentContext) {
        super(props, context);

        this.statusItems = new ObservableArray([]);
        this.searchedItems = [];

        this.performSearch([branchToVersionDescriptor(refToBranchName(props.initialTargetBranch))]);
    }

    public render() {
        const { initialTargetBranch } = this.props;

        const footerButtons: IButtonProps[] = [
            {
                id: "close",
                onClick: () => this.onDismiss(),
                text: Resources.Close
            }
        ];

        return (
            <Panel
                description={Resources.SearchPanelDescription}
                footerButtonProps={footerButtons}
                onDismiss={this.onDismiss}
                titleProps={{ text: Resources.SearchPanelHeader }}
                size={ContentSize.Large}
            >
                <div className="flex-column flex-grow rhythm-vertical-16">
                    <Observer errorMessage={this.errorMessage}>
                        {() =>
                            this.errorMessage.value && (
                                <MessageCard className="flex-noshrink" severity={MessageCardSeverity.Error} onDismiss={() => (this.errorMessage.value = "")}>
                                    {this.errorMessage.value}
                                </MessageCard>
                            )
                        }
                    </Observer>
                    <FormItem label={Resources.TargetBranchOrTag}>
                        <VersionDropdown
                            initiallySelectedVersion={
                                initialTargetBranch ? branchToVersionDescriptor(refToBranchName(initialTargetBranch)) : undefined
                            }
                            onFilterTextChanged={(event: any, text: string) => {
                                this.filterText = text;
                            }}
                            onSelect={this.onTargetBranchChanged}
                            placeholder={Resources.SelectTargetBranch}
                            providerOptions={{
                                viewTagsPivot: true,
                                otherBranchActions: [this.getSearchInFilteredRefsAction("heads")],
                                otherTagActions: [this.getSearchInFilteredRefsAction("tags")]
                            }}
                        />
                    </FormItem>
                    <Table<ISearchStatusItem>
                        className="repos-commit-exists-table"
                        columns={[nameColumn, statusColumn]}
                        itemProvider={this.statusItems}
                        onActivate={this.onActivate}
                    />
                </div>
            </Panel>
        );
    }

    private onActivate = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<ISearchStatusItem>) => {
        this.props.onActivate(event, tableRow);
    };

    private onDismiss = () => {
        this.props.onDismiss();
    };

    private onTargetBranchChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem> | null): void => {
        if (item && item.data) {
            this.performSearch([item.data.versionDescriptor]);
        }
    };

    private getSearchInFilteredRefsAction(filterPrefix: string): IButtonProps {
        return {
            text: Resources.SearchInFilteredRefs,
            tooltipProps: {
                text: Resources.SearchInFilteredRefsTooltip
            },
            onClick: () => {
                if (!this.filterText || this.filterText.length < 3) {
                    this.errorMessage.value = Resources.SearchInFilteredRefsAtLeast;
                    return;
                }

                getGitClient(this.context.pageContext)
                    .getRefs(
                        this.props.repository.id,
                        this.props.repository.project.id,
                        filterPrefix,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        this.filterText,
                        50
                    )
                    .then(refs => {
                        if (refs.length === 0) {
                            this.errorMessage.value = Resources.SearchInFilteredRefsNothingFound;
                        } else {
                            this.performSearch(refs.map((r) => {
                                if (isBranch(r.name)) {
                                    return branchToVersionDescriptor(refToBranchName(r.name));
                                } else {
                                    return tagToVersionDescriptor(refToTagName(r.name));
                                }
                            }));
                        }
                    });
            }
        };
    }

    private performSearch = (versionDescriptors: GitVersionDescriptor[]): void => {
        const newVersions = versionDescriptors.filter(v => {
            return this.searchedItems.indexOf(`${v.versionType}-${v.version}`) === -1;
        });

        if (newVersions.length > 0) {
            const gitClient: IGitRestClient = getGitClient(this.context.pageContext);
            const gitQueryBranchStatsCriteria: GitQueryBranchStatsCriteria = {
                baseCommit: commitToVersionDescriptor(this.props.commitId),
                targetCommits: newVersions
            };

            gitClient.getBranchStatsBatch(gitQueryBranchStatsCriteria, this.props.repository.id).then((stats: GitBranchStats[]) => {
                for (const stat of stats) {
                    const index = newVersions.findIndex(v => v.version === stat.name);
                    if (index >= 0) {
                        const newVersion = newVersions.splice(index, 1)[0];

                        this.statusItems.push({
                            existsInBranchOrTag: stat.behindCount === 0,
                            versionDescriptor: newVersion
                        });

                        this.searchedItems.push(`${newVersion.versionType}-${newVersion.version}`);
                    } else if (versionDescriptors.length > 1) {
                        this.errorMessage.value = Resources.SearchInFilteredRefsVersionNotRequestedError;
                    }
                }
            });
        }
    };
}

const nameColumn: ITableColumn<ISearchStatusItem> = {
    id: "nameColumn",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ISearchStatusItem>, tableItem: ISearchStatusItem) => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <Tooltip overflowOnly={true}>
                    <>
                        <Icon iconName={tableItem.versionDescriptor.versionType === GitVersionType.Tag ? "Tag" : "OpenSource"} />
                        <span className="text-ellipsis margin-left-4">{tableItem.versionDescriptor.version}</span>
                    </>
                </Tooltip>
            </SimpleTableCell>
        );
    },
    name: Resources.CheckedItems,
    width: new ObservableValue(-100)
};

const statusColumn: ITableColumn<ISearchStatusItem> = {
    id: "taggerColumn",
    renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ISearchStatusItem>, tableItem: ISearchStatusItem) => {
        const statusProps = tableItem.existsInBranchOrTag ? Statuses.Success : Statuses.Failed;
        return (
            <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                <>
                    <Status {...statusProps} key="success" size={StatusSize.m} className="status-example flex-self-center " />
                    <Tooltip overflowOnly={true}>
                        <span className="text-ellipsis margin-left-4">
                            {tableItem.existsInBranchOrTag ? Resources.IncludesCommit : Resources.DoesNotIncludeCommit}
                        </span>
                    </Tooltip>
                </>
            </SimpleTableCell>
        );
    },
    name: Resources.Status,
    width: new ObservableValue(250)
};
